import React, { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetProgramChecklistQuery,
  useAddChecklistItemsMutation,
  useDeleteChecklistItemsMutation,
  useUpdateChecklistItemMutation,
  useDeleteChecklistItemMutation,
  useGetChecklistProgressQuery,
  useAssignResearchersToItemMutation,
  useUpdateRunChecklistItemMutation,
  useFinalizeAdminReviewMutation,
  useMergeTemplateToProgramMutation,
  useGetRunChecklistQuery,
  useGetChecklistTemplatesQuery,
  useAddRunChecklistItemsMutation,
  useDeleteRunChecklistItemsMutation,
  useDeleteRunChecklistItemMutation,
  useLinkTemplateToRunMutation,
  useAssignResearchersToRunItemMutation,
  useGetChecklistTemplateQuery,
  useBulkUpdateRunStatusMutation,
  useBulkAssignResearchersToRunItemMutation,
  useBulkAssignResearchersToItemMutation,
  useBulkUpdateProgramStatusMutation,
  ProgramChecklistItem
} from "../../utils/api/endpoints/checklists/checklistApi";
import { 
  useGetResearcherReportsForProgramQuery, 
  useUpdateResearcherChecklistItemMutation 
} from "../../utils/api/endpoints/checklists/researcherChecklistApi";
import { useGetProgramQuery } from "../../utils/api/endpoints/programsApi";
import { useGetAllUsersQuery } from "../../utils/api/endpoints/usersApi";
import usePageTitle from "../../utils/hooks/usePageTitle";
import useProgramRuns from "../../utils/hooks/programs/useProgramRuns";
import toast from "react-hot-toast";
import { 
  FiPlus, FiTrash2, FiSave, FiX, FiChevronDown, FiChevronRight, 
  FiChevronLeft, FiUsers, FiUserCheck, FiEdit3, FiHome, FiLayers, 
  FiPlay, FiRefreshCw, FiSearch, FiFilter, FiCheckSquare, FiSquare, 
  FiAlertCircle, FiLink, FiFileText 
} from "react-icons/fi";
import { ChecklistGenerator } from "../../components/checklists/ChecklistGenerator";
import { ConfirmationModal } from "../../components/checklists/ConfirmationModal";

const STATUS_OPTIONS = [
  { value: "NOT_STARTED", label: "Pending", color: "gray", emoji: "⚪" },
  { value: "IN_PROGRESS", label: "Testing", color: "blue", emoji: "🔄" },
  { value: "COMPLETED", label: "Passed", color: "green", emoji: "✅" },
  { value: "NOT_APPLICABLE", label: "N/A", color: "yellow", emoji: "➖" },
  { value: "FAILED", label: "Failed", color: "red", emoji: "❌" }
];

const ASVS_LEVELS = ["L1", "L2", "L3"];

interface NewItemForm {
  category: string;
  title: string;
  description: string;
  test_objective: string;
  expected_evidence: string;
  references: string;
  requirement_id: string;
  l1: boolean;
  l2: boolean;
  l3: boolean;
  is_web: boolean;
  is_api: boolean;
  is_required: boolean;
}

const ProgramChecklistEditor: React.FC = () => {
  const { programId, runId, checklistId } = useParams<{
    programId: string;
    runId?: string;
    checklistId: string;
  }>();
  const navigate = useNavigate();

  usePageTitle("Checklist Editor");

  const { data: programChecklistData, isLoading: isLoadingProgram, refetch: refetchProgram } = useGetProgramChecklistQuery({
    programId: parseInt(programId!),
    checklistId: parseInt(checklistId!)
  }, { skip: !!runId });

  const { data: runChecklistData, isLoading: isLoadingRun, refetch: refetchRun } = useGetRunChecklistQuery({
    runId: parseInt(runId!),
    checklistId: parseInt(checklistId!)
  }, { skip: !runId });

  const masterChecklistId = runChecklistData?.data?.program_checklist_id;
  const fallbackTemplateId = runChecklistData?.data?.template_id;

  const { data: runMasterChecklistData, isLoading: isLoadingRunMaster } = useGetProgramChecklistQuery({
    programId: parseInt(programId!),
    checklistId: masterChecklistId!
  }, { skip: !runId || !masterChecklistId });

  const { data: masterTemplateData, isLoading: isLoadingTemplate } = useGetChecklistTemplateQuery(
    fallbackTemplateId!,
    { skip: !runId || !!masterChecklistId || !fallbackTemplateId }
  );

  const checklistData = runId ? runChecklistData : programChecklistData;
  const checklist = checklistData?.data;
  const masterData = runId ? (masterChecklistId ? runMasterChecklistData?.data : masterTemplateData?.data) : null;
  const isLoading = runId ? (isLoadingRun || isLoadingRunMaster || isLoadingTemplate) : isLoadingProgram;

  const { data: progressData } = useGetChecklistProgressQuery({
    programId: parseInt(programId!),
    checklistId: parseInt(checklistId!),
    runId: runId ? parseInt(runId) : undefined
  });

  const { data: programData } = useGetProgramQuery(parseInt(programId!));
  const { runs } = useProgramRuns(parseInt(programId!));
  const currentRun = runs?.find(r => r.run_id === parseInt(runId!));

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type: "info" | "danger" | "warning" | "success";
    confirmLabel?: string;
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
    type: "info",
  });

  // Fetch users by IDs from privateAccessUsers
  const { data: usersData } = useGetAllUsersQuery({
    limit: 1000,
    ids: programData?.privateAccessUsers || []
  }, {
    skip: !programData?.privateAccessUsers || programData.privateAccessUsers.length === 0
  });

  const getUserDisplayName = (userId: number) => {
    const user = usersData?.users?.find(u => u.id === userId);
    return user?.displayName || user?.username || `ID: ${userId}`;
  };

  const getUserInitial = (userId: number) => {
    const name = getUserDisplayName(userId);
    return name.charAt(0).toUpperCase();
  };

  const [addItemsMutation] = useAddChecklistItemsMutation();
  const [deleteItemMutation] = useDeleteChecklistItemMutation();
  const [assignResearchers] = useAssignResearchersToItemMutation();
  const [updateItemMutation] = useUpdateChecklistItemMutation();
  const [addRunItems] = useAddRunChecklistItemsMutation();
  const [updateRunItem] = useUpdateRunChecklistItemMutation();
  const [assignResearchersToRun] = useAssignResearchersToRunItemMutation();
  const [deleteRunItem] = useDeleteRunChecklistItemMutation();
  const [finalizeReview] = useFinalizeAdminReviewMutation();
  const [bulkUpdateRunStatus] = useBulkUpdateRunStatusMutation();
  const [bulkUpdateProgramStatus] = useBulkUpdateProgramStatusMutation();
  const [bulkAssignProgram] = useBulkAssignResearchersToItemMutation();
  const [bulkAssignRun] = useBulkAssignResearchersToRunItemMutation();
  
  // Tag templates for potential additions
  const { data: templatesData } = useGetChecklistTemplatesQuery(
    { active_only: true }
  );

  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [showAddItemForm, setShowAddItemForm] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigningItem, setAssigningItem] = useState<ProgramChecklistItem | null>(null);
  const [selectedResearchers, setSelectedResearchers] = useState<number[]>([]);
  const [newItemForm, setNewItemForm] = useState<NewItemForm>({
    category: "",
    title: "",
    description: "",
    test_objective: "",
    expected_evidence: "",
    references: "",
    requirement_id: "",
    l1: false,
    l2: false,
    l3: false,
    is_web: false,
    is_api: false,
    is_required: true
  });
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editItemForm, setEditItemForm] = useState<NewItemForm | null>(null);
  const [showSyncWizard, setShowSyncWizard] = useState(false);
  const [showMasterList, setShowMasterList] = useState(false);
  const [statusToApply, setStatusToApply] = useState<string>("COMPLETED");
  const [isBulkAssigning, setIsBulkAssigning] = useState(false);

  // Map Report Modal State
  const [showMapReportModal, setShowMapReportModal] = useState(false);
  const [mappingItem, setMappingItem] = useState<any>(null);
  const [selectedReportIds, setSelectedReportIds] = useState<number[]>([]);
  const [reportSearchTerm, setReportSearchTerm] = useState("");

  const [updateResearcherItem, { isLoading: isUpdatingReportMapping }] = useUpdateResearcherChecklistItemMutation();

  const { data: reportsData } = useGetResearcherReportsForProgramQuery(
    { 
      programId: parseInt(programId!), 
      runId: runId ? parseInt(runId) : null 
    },
    { skip: !showMapReportModal || !programId }
  );

  const researcherReports = reportsData?.data || [];

  const handleOpenMapReportModal = (item: any) => {
    setMappingItem(item);
    // Use linked_report_ids from the item's result if it exists, or from the item directly
    const itemReportIds = item.linked_report_ids || [];
    setSelectedReportIds(itemReportIds);
    setShowMapReportModal(true);
  };

  const handleCloseMapReportModal = () => {
    setShowMapReportModal(false);
    setMappingItem(null);
    setSelectedReportIds([]);
    setReportSearchTerm("");
  };

  const handleToggleReportSelection = (reportId: number) => {
    setSelectedReportIds(prev => 
      prev.includes(reportId) 
        ? prev.filter(id => id !== reportId) 
        : [...prev, reportId]
    );
  };

  const handleSaveReportMapping = async () => {
    if (!mappingItem) return;

    try {
      const formData = new FormData();
      const itemId = runId ? mappingItem.run_item_id : mappingItem.program_item_id;
      formData.append('itemId', String(itemId));
      formData.append('isSnapshot', runId ? 'true' : 'false');
      formData.append('status', 'FAILED');
      formData.append('bugFound', 'true');
      formData.append('linkedReportIds', JSON.stringify(selectedReportIds));

      await updateResearcherItem(formData).unwrap();
      toast.success("Reports mapped successfully");
      
      // Refetch the checklist data to reflect the new mapping in the table and collapsible section
      if (runId) {
        refetchRun();
      } else {
        refetchProgram();
      }
      
      handleCloseMapReportModal();
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to map reports");
    }
  };

  const filteredReports = useMemo(() => {
    if (!reportSearchTerm.trim()) {
      return [...researcherReports].sort((a, b) => new Date(b.submitted_date || 0).getTime() - new Date(a.submitted_date || 0).getTime());
    }

    const term = reportSearchTerm.trim().toLowerCase();
    // Support searching for #123 (strip the #)
    const sanitizedTerm = term.startsWith('#') ? term.substring(1) : term;

    return researcherReports
      .filter(r => {
        const titleMatch = r.report_title?.toLowerCase().includes(term);
        const idMatch = String(r.report_id).includes(sanitizedTerm);
        const severityMatch = r.severity_category?.toLowerCase().includes(term);
        
        return titleMatch || idMatch || severityMatch;
      })
      .sort((a, b) => new Date(b.submitted_date || 0).getTime() - new Date(a.submitted_date || 0).getTime());
  }, [researcherReports, reportSearchTerm]);

  // Aggregate report usage across the entire checklist for context
  const reportUsageMap = useMemo(() => {
    const map = new Map<number, { id: number, title: string }[]>();
    const currentChecklist = runId ? runChecklistData?.data : programChecklistData?.data;
    const allItems = currentChecklist?.items || [];
    
    // ID of the item we are currently mapping reports for (to exclude from "Used Elsewhere" list)
    const currentItemId = mappingItem ? (runId ? (mappingItem as any).run_item_id : mappingItem.program_item_id) : null;

    allItems.forEach((item: any) => {
      const itemId = runId ? (item as any).run_item_id : item.program_item_id;
      // Skip the current item
      if (itemId === currentItemId) return;

      const reportIds = item.linked_report_ids || [];
      reportIds.forEach((rid: number) => {
        if (!map.has(rid)) map.set(rid, []);
        map.get(rid)?.push({ id: itemId, title: item.title });
      });
    });
    return map;
  }, [runChecklistData, programChecklistData, runId, mappingItem]);
  
  // Bulk Selection & Filtering
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedMasterItems, setSelectedMasterItems] = useState<Set<number>>(new Set());
  const [selectedRunItems, setSelectedRunItems] = useState<Set<number>>(new Set());

  // Main List Filtering State
  const [listSearchTerm, setListSearchTerm] = useState("");
  const [listCategoryFilter, setListCategoryFilter] = useState("All");
  const [listStatusFilter, setListStatusFilter] = useState("All");

  const [deleteBulkItems] = useDeleteChecklistItemsMutation();
  const [deleteRunBulkItems] = useDeleteRunChecklistItemsMutation();

  // Check if a master item is already present in the run
  const isItemInRun = (masterItem: any) => {
    return checklistData?.data?.items?.some((runItem: any) => {
      if (runItem.program_item_id && masterItem.program_item_id && runItem.program_item_id === masterItem.program_item_id) {
        return true;
      }
      if (runItem.template_item_id && masterItem.template_item_id && runItem.template_item_id === masterItem.template_item_id) {
        return true;
      }
      if (runItem.title === masterItem.title && runItem.category === masterItem.category) {
        return true;
      }
      return false;
    }) || false;
  };

  const missingItems = (masterData?.items as any[])?.filter((i: any) => !isItemInRun(i)) || [];
  const missingCount = missingItems.length;

  const filteredMasterItems = (masterData?.items as any[])?.filter((item: any) => {
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         item.category?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "All" || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  }) || [];

  const masterCategories = Array.from(new Set((masterData?.items as any[])?.map((i: any) => i.category).filter(Boolean))) as string[];

  const statusOptions = ["All", "NOT_STARTED", "IN_PROGRESS", "PASSED", "FAILED", "NOT_APPLICABLE"];

  const handleToggleRunItem = (itemId: number) => {
    const newSelected = new Set(selectedRunItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedRunItems(newSelected);
  };

  const handleSelectAllRunItems = () => {
    if (selectedRunItems.size === filteredListItems.length) {
      setSelectedRunItems(new Set());
    } else {
      setSelectedRunItems(new Set(filteredListItems.map((i: any) => i.run_item_id || i.program_item_id) || []));
    }
  };

  const handleBulkUpdateStatus = async () => {
    if (selectedRunItems.size === 0) return;
    try {
      const itemIds = Array.from(selectedRunItems);
      const isSnapshot = !!runId;
      
      // We loop through items to ensure each one gets a ChecklistItemResult record for the admin
      // This ensures researchers can see the admin's bulk action in their workspace list
      const updatePromises = itemIds.map(itemId => {
        const formData = new FormData();
        formData.append('itemId', String(itemId));
        formData.append('isSnapshot', String(isSnapshot));
        formData.append('status', statusToApply);
        
        // If applying FAILED, assume it's an authoritative status without specific reports 
        // (Admin can link reports individually if needed)
        if (statusToApply === 'FAILED') {
          formData.append('bugFound', 'true');
        } else {
          formData.append('bugFound', 'false');
        }
        
        return updateResearcherItem(formData).unwrap();
      });

      await Promise.all(updatePromises);

      toast.success(`Updated ${selectedRunItems.size} items to ${statusToApply.replace('_', ' ')} and recorded results`);
      setSelectedRunItems(new Set());
      
      // Refetch to sync UI
      if (runId) refetchRun();
      else refetchProgram();
      
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to bulk update items");
    }
  };

  const handleBulkAssign = () => {
    if (selectedRunItems.size === 0) return;
    setIsBulkAssigning(true);
    setAssigningItem(null); // No single item
    setSelectedResearchers([]);
    setShowAssignModal(true);
  };


  const filteredListItems = useMemo(() => {
    if (!checklist?.items) return [];
    return checklist.items.filter((item: any) => {
      const matchesSearch =
        item.requirement_id?.toLowerCase().includes(listSearchTerm.toLowerCase()) ||
        item.title?.toLowerCase().includes(listSearchTerm.toLowerCase());
      
      const matchesCategory = listCategoryFilter === "All" || item.category === listCategoryFilter;
      
      let matchesStatus = true;
      if (listStatusFilter !== "All") {
        const hasIssues = item.bug_found || (item.linked_report_ids && item.linked_report_ids.length > 0);
        
        if (listStatusFilter === "PASSED") {
          // Passed = COMPLETED only AND no issues (excludes N_A)
          matchesStatus = item.status === "COMPLETED" && !hasIssues;
        } else if (listStatusFilter === "FAILED") {
          // Failed = explicitly FAILED or has issues
          matchesStatus = item.status === "FAILED" || hasIssues;
        } else {
          // Direct status match (NOT_STARTED, IN_PROGRESS, NOT_APPLICABLE, etc.)
          matchesStatus = item.status === listStatusFilter;
        }
      }

      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [checklist?.items, listSearchTerm, listCategoryFilter, listStatusFilter]);

  const listCategories = useMemo(() => {
    if (!checklist?.items) return ["All"];
    const cats = Array.from(new Set(checklist.items.map((it: any) => it.category).filter(Boolean)));
    return ["All", ...cats.sort()];
  }, [checklist?.items]);

  const handleBulkDelete = async () => {
    if (selectedRunItems.size === 0) return;

    // Check if any selected items have progress
    const itemsWithProgress: string[] = [];
    selectedRunItems.forEach((itemId) => {
      const item = checklist?.items?.find((i: any) => (runId ? i.run_item_id : i.program_item_id) === itemId);
      if (item) {
        const hasProgress =
          item.status !== "NOT_STARTED" ||
          (item.linked_report_ids && item.linked_report_ids.length > 0) ||
          (item.evidence_files && item.evidence_files.length > 0) ||
          item.bug_found;
        if (hasProgress) {
          itemsWithProgress.push(item.title);
        }
      }
    });

    if (itemsWithProgress.length > 0) {
      toast.error(`Cannot delete selection: ${itemsWithProgress.length} of the selected test cases have active testing progress, uploaded evidence, or linked findings. To preserve audit trail integrity, they cannot be removed.`);
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: "Delete Items",
      message: `Are you sure you want to delete ${selectedRunItems.size} items?`,
      type: "danger",
      confirmLabel: "Delete",
      onConfirm: async () => {
        try {
          if (runId) {
            await deleteRunBulkItems({
              runId: parseInt(runId!),
              itemIds: Array.from(selectedRunItems)
            }).unwrap();
          } else {
            await deleteBulkItems({
              programId: parseInt(programId!),
              checklistId: parseInt(checklistId!),
              itemIds: Array.from(selectedRunItems)
            }).unwrap();
          }
          toast.success("Items deleted successfully");
          setSelectedRunItems(new Set());
          if (runId) refetchRun();
          else refetchProgram();
        } catch (err: any) {
          toast.error(err?.data?.message || "Failed to delete items");
        }
      }
    });
  };

  const handleBulkDeleteDummyPlaceholder = async () => {
    
    try {
      if (runId) {
        await deleteRunBulkItems({
          runId: parseInt(runId!),
          itemIds: Array.from(selectedRunItems)
        }).unwrap();
      } else {
        await deleteBulkItems({
          programId: parseInt(programId!),
          checklistId: parseInt(checklistId!),
          itemIds: Array.from(selectedRunItems)
        }).unwrap();
      }
      toast.success("Items deleted successfully");
      setSelectedRunItems(new Set());
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to delete items");
    }
  };

  const handleToggleMasterItem = (itemId: number) => {
    const newSelected = new Set(selectedMasterItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedMasterItems(newSelected);
  };

  const handleSelectAllMaster = () => {
    const availableItems = filteredMasterItems.filter(i => !isItemInRun(i));
    if (selectedMasterItems.size === availableItems.length) {
      setSelectedMasterItems(new Set());
    } else {
      setSelectedMasterItems(new Set(availableItems.map((i: any) => i.program_item_id || i.item_id)));
    }
  };

  const handleBulkAddFromMaster = async () => {
    try {
      if (selectedMasterItems.size === 0) return;
      
      const itemsToAdd = (masterData?.items as any[]).filter((i: any) => 
        selectedMasterItems.has(i.program_item_id || i.item_id)
      ).map((masterItem: any) => ({
        template_item_id: masterItem.template_item_id || masterItem.item_id,
        program_item_id: masterItem.program_item_id || null,
        category: masterItem.category,
        title: masterItem.title,
        description: masterItem.description,
        test_objective: masterItem.test_objective,
        expected_evidence: masterItem.expected_evidence,
        references: masterItem.references,
        requirement_id: masterItem.requirement_id,
        l1: masterItem.l1,
        l2: masterItem.l2,
        l3: masterItem.l3,
        is_web: masterItem.is_web,
        is_api: masterItem.is_api,
        is_required: masterItem.is_required
      }));

      if (runId) {
        await addRunItems({
          runId: parseInt(runId!),
          checklistId: parseInt(checklistId!),
          items: itemsToAdd
        }).unwrap();
      } else {
        await addItemsMutation({
          programId: parseInt(programId!),
          checklistId: parseInt(checklistId!),
          items: itemsToAdd
        }).unwrap();
      }

      toast.success(`${itemsToAdd.length} items added successfully`);
      setSelectedMasterItems(new Set());
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to bulk add items");
    }
  };

  const handleToggleExpand = (itemId: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const handleAddItem = async () => {
    if (!newItemForm.title.trim()) {
      toast.error("Title is required");
      return;
    }

    try {
      if (runId) {
        await addRunItems({
          runId: parseInt(runId!),
          checklistId: parseInt(checklistId!),
          items: [newItemForm]
        }).unwrap();
      } else {
        await addItemsMutation({
          programId: parseInt(programId!),
          checklistId: parseInt(checklistId!),
          items: [newItemForm]
        }).unwrap();
      }
      toast.success("Item added successfully");
      setShowAddItemForm(false);
      setNewItemForm({
        category: "",
        title: "",
        description: "",
        test_objective: "",
        expected_evidence: "",
        references: "",
        requirement_id: "",
        l1: false,
        l2: false,
        l3: false,
        is_web: false,
        is_api: false,
        is_required: true
      });
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to add item");
    }
  };

  const handleAddMasterItemToRun = async (masterItem: any) => {
    try {
      if (!runId) return;

      const newItem = {
        template_item_id: masterItem.template_item_id || masterItem.item_id,
        program_item_id: masterItem.program_item_id || null,
        category: masterItem.category,
        title: masterItem.title,
        description: masterItem.description,
        test_objective: masterItem.test_objective,
        expected_evidence: masterItem.expected_evidence,
        references: masterItem.references,
        requirement_id: masterItem.requirement_id,
        l1: masterItem.l1,
        l2: masterItem.l2,
        l3: masterItem.l3,
        is_web: masterItem.is_web,
        is_api: masterItem.is_api,
        is_required: masterItem.is_required
      };

      await addRunItems({
        runId: parseInt(runId!),
        checklistId: parseInt(checklistId!),
        items: [newItem]
      }).unwrap();

      toast.success("Item added from master successfully");
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to add item from master");
    }
  };

  const handleDeleteItem = async (itemId: number) => {
    const item = checklist?.items?.find((i: any) => (runId ? i.run_item_id : i.program_item_id) === itemId);
    const hasProgress = item && (
      item.status !== "NOT_STARTED" ||
      (item.linked_report_ids && item.linked_report_ids.length > 0) ||
      (item.evidence_files && item.evidence_files.length > 0) ||
      item.bug_found
    );

    if (hasProgress) {
      toast.error(`Cannot delete "${item.title}": This test case has active testing progress, uploaded evidence, or linked vulnerability findings. To preserve audit trail integrity, it cannot be removed.`);
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: "Delete Item",
      message: "Are you sure you want to delete this item?",
      type: "danger",
      confirmLabel: "Delete",
      onConfirm: async () => {
        try {
          if (runId) {
            await deleteRunItem({
              runId: parseInt(runId!),
              itemId
            }).unwrap();
          } else {
            await deleteItemMutation({
              programId: parseInt(programId!),
              checklistId: parseInt(checklistId!),
              itemId
            }).unwrap();
          }
          toast.success("Item deleted successfully");
          if (runId) refetchRun();
          else refetchProgram();
        } catch (err: any) {
          toast.error(err?.data?.message || "Failed to delete item");
        }
      }
    });
  };

  const handleDeleteItemOld = async (itemId: number) => {

    try {
      if (runId) {
        await deleteRunItem({
          runId: parseInt(runId!),
          itemId
        }).unwrap();
      } else {
        await deleteItemMutation({
          programId: parseInt(programId!),
          checklistId: parseInt(checklistId!),
          itemId
        }).unwrap();
      }
      toast.success("Item deleted successfully");
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to delete item");
    }
  };

  const handleOpenAssignModal = (item: ProgramChecklistItem) => {
    setAssigningItem(item);
    setSelectedResearchers(item.assigned_to || []);
    setShowAssignModal(true);
  };

  const handleCloseAssignModal = () => {
    setShowAssignModal(false);
    setAssigningItem(null);
    setSelectedResearchers([]);
    setIsBulkAssigning(false);
  };

  const handleToggleResearcher = (userId: number) => {
    setSelectedResearchers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleSaveAssignment = async () => {
    if (!assigningItem && !isBulkAssigning) return;

    try {
      if (isBulkAssigning) {
        if (runId) {
          await bulkAssignRun({
            runId: parseInt(runId!),
            itemIds: Array.from(selectedRunItems),
            assigned_to: selectedResearchers.length > 0 ? selectedResearchers : null
          }).unwrap();
        } else {
          await bulkAssignProgram({
            programId: parseInt(programId!),
            checklistId: parseInt(checklistId!),
            itemIds: Array.from(selectedRunItems),
            assigned_to: selectedResearchers.length > 0 ? selectedResearchers : null
          }).unwrap();
        }
        toast.success(`Researchers assigned to ${selectedRunItems.size} items successfully`);
        setSelectedRunItems(new Set());
      } else if (assigningItem) {
        if (runId) {
          await assignResearchersToRun({
            runId: parseInt(runId!),
            itemId: (assigningItem as any).run_item_id || assigningItem.program_item_id,
            assigned_to: selectedResearchers.length > 0 ? selectedResearchers : null
          }).unwrap();
        } else {
          await assignResearchers({
            programId: parseInt(programId!),
            checklistId: parseInt(checklistId!),
            itemId: assigningItem.program_item_id,
            assigned_to: selectedResearchers.length > 0 ? selectedResearchers : null
          }).unwrap();
        }

        toast.success(
          selectedResearchers.length > 0
            ? "Researchers assigned successfully"
            : "Assignment cleared - all researchers can now see this item"
        );
      }

      handleCloseAssignModal();
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to assign researchers");
    }
  };

  const handleStartEditItem = (item: ProgramChecklistItem) => {
    const itemId = runId ? (item as any).run_item_id : item.program_item_id;
    setEditingItemId(itemId);
    setEditItemForm({
      category: item.category || "",
      title: item.title || "",
      description: item.description || "",
      test_objective: item.test_objective || "",
      expected_evidence: item.expected_evidence || "",
      references: item.references || "",
      requirement_id: item.requirement_id || "",
      l1: item.l1 || false,
      l2: item.l2 || false,
      l3: item.l3 || false,
      is_web: item.is_web || false,
      is_api: item.is_api || false,
      is_required: item.is_required ?? true
    });
  };

  const handleCancelEditItem = () => {
    setEditingItemId(null);
    setEditItemForm(null);
  };

  const handleSaveEditItem = async () => {
    if (!editingItemId || !editItemForm) return;

    if (!editItemForm.title.trim()) {
      toast.error("Title is required");
      return;
    }

    try {
      if (runId) {
        await updateRunItem({
          runId: parseInt(runId!),
          itemId: editingItemId,
          data: {
            category: editItemForm.category,
            title: editItemForm.title,
            description: editItemForm.description,
            test_objective: editItemForm.test_objective,
            expected_evidence: editItemForm.expected_evidence,
            references: editItemForm.references,
            requirement_id: editItemForm.requirement_id,
            l1: editItemForm.l1,
            l2: editItemForm.l2,
            l3: editItemForm.l3,
            is_required: editItemForm.is_required
          }
        }).unwrap();
      } else {
        await updateItemMutation({
          programId: parseInt(programId!),
          checklistId: parseInt(checklistId!),
          itemId: editingItemId,
          data: {
            category: editItemForm.category,
            title: editItemForm.title,
            description: editItemForm.description,
            test_objective: editItemForm.test_objective,
            expected_evidence: editItemForm.expected_evidence,
            references: editItemForm.references,
            requirement_id: editItemForm.requirement_id,
            l1: editItemForm.l1,
            l2: editItemForm.l2,
            l3: editItemForm.l3,
            is_required: editItemForm.is_required
          }
        }).unwrap();
      }

      toast.success("Item updated successfully");
      setEditingItemId(null);
      setEditItemForm(null);
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to update item");
    }
  };

  const handleUpdateStatus = async (itemId: number, newStatus: any) => {
    try {
      const statusValue = newStatus as "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED" | "NOT_APPLICABLE" | "FAILED";
      
      // Use the researcher update API even for admins so that it creates a ChecklistItemResult record
      // This ensures researchers can see "Admin: Passed" or "Admin: Failed" in their testers list.
      const formData = new FormData();
      formData.append('itemId', String(itemId));
      formData.append('isSnapshot', runId ? 'true' : 'false');
      formData.append('status', statusValue);
      
      if (statusValue === 'FAILED') {
        formData.append('bugFound', 'true');
      } else {
        formData.append('bugFound', 'false');
      }

      await updateResearcherItem(formData).unwrap();
      
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
      
      // Refetch to ensure item-level status (aggregated) is synced in the grid
      if (runId) refetchRun();
      else refetchProgram();

    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to update status");
    }
  };

  const handleFinalizeReview = async () => {
    setConfirmModal({
      isOpen: true,
      title: "Finalize Admin Review",
      message: "Are you sure you want to finalize the admin review? Researchers will now be able to see and work on this checklist.",
      type: "success",
      confirmLabel: "Finalize Review",
      onConfirm: async () => {
        try {
          await finalizeReview({
            programId: parseInt(programId!),
            checklistId: parseInt(checklistId!)
          }).unwrap();
          toast.success("Review finalized successfully");
        } catch (err: any) {
          toast.error(err?.data?.message || "Failed to finalize review");
        }
      }
    });
  };

  const handleFinalizeReviewOld = async () => {

    try {
      await finalizeReview({
        programId: parseInt(programId!),
        checklistId: parseInt(checklistId!)
      }).unwrap();
      toast.success("Review finalized successfully");
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to finalize review");
    }
  };

  const getOutcomeBadgeForItem = (item: ProgramChecklistItem) => {
    const performed =
      item.status === "COMPLETED" ||
      item.status === "NOT_APPLICABLE" ||
      item.status === "FAILED";

    if (!performed) return null;

    const failed =
      item.status === "FAILED" ||
      item.bug_found === true ||
      (item.linked_report_ids && item.linked_report_ids.length > 0);

    if (failed) {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700">
          ❌ Failed
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
        ✅ Passed
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="montserrat-font flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="mx-auto mb-4 h-16 w-16 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
          <p className="text-xl font-bold text-gray-900">Loading Checklist...</p>
          <p className="mt-2 text-sm text-gray-600">Please wait</p>
        </div>
      </div>
    );
  }

  const progress = progressData?.data;
  const isV2Continuous = programData?.architecture_version === 2 && programData?.scheduling_type === "continuous";

  // Derive pass/fail metrics based on executed tests and bugs
  // A test is considered "performed" when status is COMPLETED, NOT_APPLICABLE or FAILED
  // A performed test is FAILED if a bug was found or reports are linked
  // Performed tests without bugs are counted as PASSED
  const items = checklist?.items || [];

  const performedItems = items.filter(item =>
    item.status === "COMPLETED" ||
    item.status === "NOT_APPLICABLE" ||
    item.status === "FAILED"
  );

  const failedTests = performedItems.filter(item =>
    item.status === "FAILED" ||
    item.bug_found === true ||
    (item.linked_report_ids && item.linked_report_ids.length > 0)
  ).length;

  const passedTests = items.filter(item => 
    item.status === "COMPLETED" && 
    !item.bug_found && 
    !(item.linked_report_ids && item.linked_report_ids.length > 0)
  ).length;
  const naItems = items.filter(i => i.status === 'NOT_APPLICABLE').length;
  const completionPercentage = items.length > 0 ? Math.round((performedItems.length / items.length) * 100) : 0;

  if (!checklist) {
    return (
      <div className="montserrat-font flex min-h-screen items-center justify-center bg-slate-50">
        <div className="max-w-md text-center">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-red-100">
            <svg className="h-10 w-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="mb-2 text-xl font-bold text-gray-900">Checklist Not Found</h3>
          <p className="mb-6 text-sm text-gray-600">The checklist you're looking for doesn't exist or you don't have access to it.</p>
          <button
            onClick={() => navigate(`/dashboard/programs/${programId}`)}
            className="rounded-xl bg-blue-600 px-6 py-3 font-semibold text-white transition-all hover:bg-blue-700"
          >
            Back to Program
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="montserrat-font min-h-screen bg-slate-50">
      {/* Professional Compact Breadcrumbs */}
      <div className="bg-white border-b border-gray-100 px-6 py-2">
        <div className="mx-auto max-w-7xl flex items-center gap-2.5 text-[10px] font-black uppercase tracking-[0.1em] text-gray-400">
          <button 
            onClick={() => navigate(`/dashboard/programs/${programId}`)}
            className="hover:text-blue-600 transition-all flex items-center gap-1.5 group"
          >
            <FiHome className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
            <span className="border-b border-transparent group-hover:border-blue-600">{programData?.title || "Program"}</span>
          </button>
          <FiChevronRight className="text-gray-300 w-3 h-3" />
          {runId ? (
            <div className="flex items-center gap-2">
              <span className="text-blue-600 font-bold flex items-center gap-1">
                <FiPlay className="w-3 h-3" />
                Run: {currentRun?.run_title || `#${runId}`}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-[#58CC02] font-bold flex items-center gap-1">
                <FiLayers className="w-3 h-3" />
                Master Checklist
              </span>
            </div>
          )}
          <FiChevronRight className="text-gray-300 w-3 h-3" />
          <span className="text-gray-900 font-black">{checklist.checklist_name}</span>
        </div>
      </div>

      {/* NEW Standalone Header Card */}
      <div className="mx-auto max-w-7xl px-6 pt-6 mb-6">
        <div className="bg-white px-8 py-8 rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <button
                onClick={() => navigate(`/dashboard/programs/${programId}`)}
                className="flex items-center justify-center w-12 h-12 rounded-xl border border-gray-100 text-gray-400 hover:bg-gray-50 hover:text-gray-900 transition-all shadow-sm"
                title="Back to Program"
              >
                <FiChevronLeft className="h-6 w-6" />
              </button>
              
              <div className="flex flex-col">
                <div className="flex items-center gap-3 mb-1.5">
                  <h1 className="text-xl font-bold text-[#010D2C] tracking-tight">
                    {checklist.checklist_name}
                  </h1>
                  {checklist.framework && (
                    <span className="px-3 py-1 rounded-lg text-[11px] font-black uppercase tracking-widest bg-blue-50 text-blue-600 border border-blue-100">
                      {checklist.framework.replace(/_/g, ' ')}
                    </span>
                  )}
                  {(checklist as any).is_custom && (
                    <span className="px-3 py-1 rounded-lg text-[11px] font-black uppercase tracking-widest bg-blue-50 text-blue-600 border border-blue-100">
                      CUSTOM
                    </span>
                  )}
                </div>
                {checklist.description && (
                  <p className="text-[12px] font-bold text-gray-400 uppercase tracking-wider max-w-4xl line-clamp-1 opacity-80">
                    {checklist.description}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-4">
              {selectedRunItems.size > 0 && (
                <div className="flex items-center gap-2 p-1.5 bg-red-50 rounded-xl border border-red-100 mr-2">
                  <button
                    onClick={handleBulkDelete}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-red-600 text-white text-[11px] font-black uppercase tracking-widest shadow-md hover:bg-red-700 transition-all"
                  >
                    <FiTrash2 className="w-4 h-4" />
                    Delete {selectedRunItems.size}
                  </button>
                </div>
              )}

              <div className="flex items-center gap-3">
                {runId && (
                  <>
                    <button
                      onClick={() => setShowSyncWizard(true)}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-100 bg-white text-gray-600 text-[11px] font-black uppercase tracking-widest hover:bg-gray-50 transition-all shadow-sm"
                    >
                      <FiRefreshCw className="w-4 h-4 text-blue-500" />
                      Sync
                    </button>
                    <button
                      onClick={() => setShowMasterList(!showMasterList)}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-100 bg-white text-gray-600 text-[11px] font-black uppercase tracking-widest hover:bg-gray-50 transition-all shadow-sm"
                    >
                      <FiLayers className="w-4 h-4 text-purple-500" />
                      Master ({missingCount})
                    </button>
                  </>
                )}
                
                <button
                  onClick={() => setShowAddItemForm(true)}
                  className="flex items-center gap-2 px-8 py-3 rounded-xl bg-[#010D2C] text-[#58CC02] text-[12px] font-black uppercase tracking-widest shadow-xl hover:scale-[1.02] transition-all hover:shadow-2xl ring-4 ring-[#010D2C]/5"
                >
                  <FiPlus className="w-5 h-5" />
                  Add Case
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sync Wizard Modal */}
      {showSyncWizard && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#010D2C]/60 backdrop-blur-sm">
          <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
             <ChecklistGenerator 
               programId={parseInt(programId!)} 
               runId={parseInt(runId!)}
               asset={programData?.asset}
               onClose={() => setShowSyncWizard(false)} 
             />
          </div>
        </div>
      )}

      <div className="mx-auto max-w-7xl px-6">
        {/* NEW Premium Navy Completion Status Bar */}
        <div className="mb-8 rounded-xl bg-[#010D2C] p-6 shadow-xl border border-slate-800">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            {/* Left Section: Circular Progress */}
            <div className="flex items-center gap-6">
              <div className="relative flex h-20 w-20 items-center justify-center">
                <svg className="h-full w-full -rotate-90">
                  <circle
                    className="text-slate-700"
                    strokeWidth="6"
                    stroke="currentColor"
                    fill="transparent"
                    r="34"
                    cx="40"
                    cy="40"
                  />
                  <circle
                    className="text-[#58CC02] transition-all duration-1000 ease-out"
                    strokeWidth="6"
                    strokeDasharray={2 * Math.PI * 34}
                    strokeDashoffset={2 * Math.PI * 34 * (1 - completionPercentage / 100)}
                    strokeLinecap="round"
                    stroke="currentColor"
                    fill="transparent"
                    r="34"
                    cx="40"
                    cy="40"
                  />
                </svg>
                <span className="absolute text-lg font-black text-white">{completionPercentage}%</span>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white leading-tight">Completion Status</h3>
                <p className="text-sm font-medium text-slate-400">{items.length} Total Cases</p>
              </div>
            </div>

            {/* Center Section: Progress Bar */}
            <div className="flex-1 w-full max-w-xl">
              <div className="h-2 w-full rounded-full bg-slate-800 overflow-hidden">
                <div 
                  className="h-full bg-white transition-all duration-1000 ease-out"
                  style={{ width: `${completionPercentage}%` }}
                ></div>
              </div>
            </div>

            {/* Right Section: Stats Row */}
            <div className="flex items-center gap-12">
              <div className="text-center">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Performed</p>
                <p className="text-xl font-black text-emerald-500">{performedItems.length}</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Passed</p>
                <p className="text-xl font-black text-blue-400">{passedTests}</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Failed</p>
                <p className="text-xl font-black text-red-500">{failedTests}</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">N/A</p>
                <p className="text-xl font-black text-orange-400">{naItems}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Add New Item Form */}
        {showAddItemForm && (
          <div className="mb-6 rounded-xl bg-white p-6 shadow-lg ring-1 ring-gray-200">
            <div className="mb-5 flex items-center justify-between border-b border-gray-200 pb-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100">
                  <FiPlus className="h-5 w-5 text-green-600" />
                </div>
                <h2 className="text-base font-bold text-gray-900">Add New Test Case</h2>
              </div>
              <button
                onClick={() => setShowAddItemForm(false)}
                className="rounded-lg p-2 text-gray-400 transition-all hover:bg-gray-100 hover:text-gray-600"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">Req ID</label>
                  <input
                    type="text"
                    value={newItemForm.requirement_id}
                    onChange={(e) => setNewItemForm({ ...newItemForm, requirement_id: e.target.value })}
                    className="w-full rounded-xl border-2 border-gray-300 px-4 py-2.5 text-sm font-medium transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    placeholder="e.g., 1.1.1"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">Levels</label>
                  <div className="flex gap-2">
                    {ASVS_LEVELS.map(level => {
                      const key = level.toLowerCase() as "l1" | "l2" | "l3";
                      return (
                        <button
                          key={level}
                          type="button"
                          onClick={() => setNewItemForm({ ...newItemForm, [key]: !newItemForm[key] })}
                          className={`flex-1 rounded-xl border-2 py-2.5 text-xs font-bold transition-all ${
                            newItemForm[key] ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-300 text-gray-400 hover:border-blue-300'
                          }`}
                        >
                          {level}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-gray-700">Title *</label>
                <input
                  type="text"
                  value={newItemForm.title}
                  onChange={(e) => setNewItemForm({ ...newItemForm, title: e.target.value })}
                  className="w-full rounded-xl border-2 border-gray-300 px-4 py-2.5 text-sm font-medium transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  placeholder="Enter test case title"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-gray-700">Description</label>
                <textarea
                  value={newItemForm.description}
                  onChange={(e) => setNewItemForm({ ...newItemForm, description: e.target.value })}
                  rows={3}
                  className="w-full rounded-xl border-2 border-gray-300 px-4 py-2.5 text-sm font-medium transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  placeholder="Describe what needs to be tested"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-gray-700">Test Objective</label>
                <textarea
                  value={newItemForm.test_objective}
                  onChange={(e) => setNewItemForm({ ...newItemForm, test_objective: e.target.value })}
                  rows={2}
                  className="w-full rounded-xl border-2 border-gray-300 px-4 py-2.5 text-sm font-medium transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  placeholder="What is the goal of this test?"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-gray-700">Expected Evidence</label>
                <textarea
                  value={newItemForm.expected_evidence}
                  onChange={(e) => setNewItemForm({ ...newItemForm, expected_evidence: e.target.value })}
                  rows={2}
                  className="w-full rounded-xl border-2 border-gray-300 px-4 py-2.5 text-sm font-medium transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  placeholder="What evidence should be provided?"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-gray-700">References</label>
                <input
                  type="text"
                  value={newItemForm.references}
                  onChange={(e) => setNewItemForm({ ...newItemForm, references: e.target.value })}
                  className="w-full rounded-xl border-2 border-gray-300 px-4 py-2.5 text-sm font-medium transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  placeholder="Links or references (comma-separated)"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_required"
                  checked={newItemForm.is_required}
                  onChange={(e) => setNewItemForm({ ...newItemForm, is_required: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
                />
                <label htmlFor="is_required" className="text-sm font-semibold text-gray-700">
                  This test case is required
                </label>
              </div>

              <div className="flex items-center gap-3 border-t border-gray-200 pt-4">
                <button
                  onClick={handleAddItem}
                  className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-green-600 to-green-700 px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition-all hover:from-green-700 hover:to-green-800 hover:shadow-xl"
                >
                  <FiSave className="h-4 w-4" />
                  Add Test Case
                </button>
                <button
                  onClick={() => setShowAddItemForm(false)}
                  className="inline-flex items-center gap-2 rounded-xl border-2 border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50"
                >
                  <FiX className="h-4 w-4" />
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* High-Density Checklist Items Grid */}
        {!checklist.items || checklist.items.length === 0 ? (
          <div className="rounded-2xl bg-white p-12 text-center shadow-sm ring-1 ring-gray-100">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-50 border border-gray-100">
              <FiLayers className="h-8 w-8 text-gray-300" />
            </div>
            <h3 className="text-base font-black text-gray-900">No Test Cases Yet</h3>
            <p className="text-xs text-gray-500 mt-1">Get started by adding test cases from master or manually.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200 overflow-hidden">
            {/* Bulk Actions Bar */}
            {selectedRunItems.size > 0 && (
              <div className="bg-blue-50/50 px-6 py-3 border-b border-blue-100 flex items-center justify-between animate-in fade-in slide-in-from-top-1">
                <div className="flex items-center gap-4">
                   <div className="flex items-center gap-2 px-3 py-1 bg-blue-600 rounded-full text-white shadow-sm ring-1 ring-blue-700">
                      <FiCheckSquare className="w-3.5 h-3.5" />
                      <span className="text-[10px] font-black uppercase tracking-widest">{selectedRunItems.size} Items Selected</span>
                   </div>
                   <div className="flex items-center gap-2 pl-4 border-l border-blue-200">
                      <select 
                        value={statusToApply}
                        onChange={(e) => setStatusToApply(e.target.value)}
                        className="rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-xs font-bold text-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all cursor-pointer"
                      >
                        {STATUS_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.emoji} {opt.label}</option>
                        ))}
                      </select>
                      <button
                        onClick={handleBulkUpdateStatus}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-1.5 text-[10px] font-black uppercase tracking-widest text-white shadow-sm transition-all hover:bg-blue-700 hover:shadow-md active:scale-95"
                      >
                        Apply Status
                      </button>
                      <button
                        onClick={handleBulkAssign}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-1.5 text-[10px] font-black uppercase tracking-widest text-white shadow-sm transition-all hover:bg-indigo-700 hover:shadow-md active:scale-95"
                      >
                        <FiUsers className="w-3.5 h-3.5" />
                        Bulk Assign
                      </button>
                   </div>
                </div>
                <button
                  onClick={handleBulkDelete}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-4 py-1.5 text-[10px] font-black uppercase tracking-widest text-red-600 shadow-sm transition-all hover:bg-red-50 hover:text-red-700 active:scale-95"
                >
                  <FiTrash2 className="h-3.5 h-3.5" />
                  Bulk Delete
                </button>
              </div>
            )}

            {/* Filter Bar */}
            <div className="mb-6 p-4 bg-gray-50/50 rounded-xl border border-gray-100 flex flex-wrap items-center gap-4 mx-6 mt-4">
              <div className="relative flex-1 min-w-[240px]">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search items by ID or title..."
                  value={listSearchTerm}
                  onChange={(e) => setListSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none text-sm"
                />
              </div>

              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-wider">Category</span>
                <select
                  value={listCategoryFilter}
                  onChange={(e) => setListCategoryFilter(e.target.value)}
                  className="bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-xs font-semibold focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none min-w-[140px]"
                >
                  {listCategories.map((cat: any) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-wider">Status</span>
                <select
                  value={listStatusFilter}
                  onChange={(e) => setListStatusFilter(e.target.value)}
                  className="bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-xs font-semibold focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none min-w-[140px]"
                >
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status.replace(/_/g, " ")}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ml-auto text-[10px] font-black text-gray-400 uppercase tracking-widest">
                Showing {filteredListItems.length} / {checklist?.items?.length || 0}
              </div>
            </div>

            {/* List Header Labels */}
            <div className="grid grid-cols-[48px_48px_1fr_200px_160px_160px] items-center gap-4 border-b border-gray-100 bg-gray-50/50 px-6 py-2.5">
              <div className="flex justify-center">
                 <div className="cursor-pointer text-gray-400 hover:text-blue-500 transition-colors" onClick={handleSelectAllRunItems} title="Select All">
                   {selectedRunItems.size === (checklist?.items?.length || 0) && (checklist?.items?.length || 0) > 0 ? <FiCheckSquare className="w-5 h-5 text-blue-600" /> : <FiSquare className="w-5 h-5" />}
                 </div>
              </div>
              <div className="text-[10px] font-black text-gray-400 uppercase text-center">#</div>
              <div className="text-[10px] font-black text-gray-400 uppercase">Test Case Details</div>
              <div className="text-[10px] font-black text-gray-400 uppercase">Assignment & Insights</div>
              <div className="text-[10px] font-black text-gray-400 uppercase">Status</div>
              <div className="text-[10px] font-black text-gray-400 uppercase text-right pr-4">Actions</div>
            </div>

            <div className="divide-y divide-gray-100">

              {filteredListItems.map((item: any, index: number) => {
                const currentItemId = runId ? (item as any).run_item_id : item.program_item_id;
                const isExpanded = expandedItems.has(currentItemId);
                const isSelected = selectedRunItems.has(currentItemId);

                return (
                  <div key={currentItemId} className={`group transition-all ${isSelected ? 'bg-blue-50/30' : 'hover:bg-slate-50/50'}`}>
                    <div className="grid grid-cols-[48px_48px_1fr_200px_160px_160px] items-start gap-4 px-6 py-2.5">
                      {/* Selection */}
                      <div className="flex justify-center">
                        <div 
                          className="cursor-pointer text-gray-400 hover:text-blue-500"
                          onClick={() => handleToggleRunItem(currentItemId)}
                        >
                          {isSelected ? <FiCheckSquare className="w-5 h-5 text-blue-600" /> : <FiSquare className="w-5 h-5" />}
                        </div>
                      </div>

                      {/* Index */}
                      <div className="text-xs font-black text-gray-400 text-center">{index + 1}</div>

                      {/* Title & Metadata */}
                      <div className="flex flex-col min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/dashboard/checklist-review/items/${currentItemId}`);
                            }}
                            className={`text-sm font-bold transition-colors cursor-pointer hover:underline underline-offset-4 decoration-current break-all ${isExpanded ? 'text-blue-600' : 'text-gray-900 hover:text-blue-600'}`}
                            title="Open in Workspace"
                          >
                            {item.title}
                          </h3>
                          <div className="flex flex-wrap items-center gap-2 cursor-pointer" onClick={() => handleToggleExpand(currentItemId)}>
                            {item.is_required && <span className="text-[9px] font-black text-purple-600 uppercase bg-purple-50 px-1.5 py-0.5 rounded ring-1 ring-purple-100">Required</span>}
                            {item.requirement_id && <span className="text-[9px] font-black text-gray-500 uppercase bg-gray-50 px-1.5 py-0.5 rounded border border-gray-200">{item.requirement_id}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 mt-0.5">
                          {item.category && <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider truncate max-w-[200px]">{item.category}</span>}
                          {checklist.framework === "ASVS" && (
                            <div className="flex gap-1">
                              {ASVS_LEVELS.map(level => {
                                const key = level.toLowerCase() as "l1" | "l2" | "l3";
                                if (!(item as any)[key]) return null;
                                return <span key={level} className="text-[8px] font-black text-blue-500">{level}</span>;
                              })}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Assignment & Insights */}
                      <div className="flex items-center gap-3">
                         {item.assigned_to && item.assigned_to.length > 0 ? (
                           <div className="flex -space-x-2">
                             {item.assigned_to.slice(0, 3).map((uid: number, idx: number) => {
                               const name = getUserDisplayName(uid);
                               const initial = getUserInitial(uid);
                               return (
                                 <div key={idx} className="h-6 w-6 rounded-full bg-blue-100 border-2 border-white flex items-center justify-center text-[8px] font-black text-blue-600 shadow-sm" title={name}>
                                   {initial}
                                 </div>
                               );
                             })}
                             {item.assigned_to.length > 3 && (
                               <div className="h-6 w-6 rounded-full bg-gray-100 border-2 border-white flex items-center justify-center text-[8px] font-black text-gray-400">
                                 +{item.assigned_to.length - 3}
                               </div>
                             )}
                           </div>
                         ) : (
                           <span className="text-[10px] font-bold text-gray-300 italic">Unassigned</span>
                         )}
                         <div className="flex items-center gap-1.5 ml-auto pr-4">
                           {item.bug_found && <FiAlertCircle className="w-4 h-4 text-red-500" title="Bug Found" />}
                           {item.linked_report_ids && item.linked_report_ids.length > 0 && <FiLink className="w-4 h-4 text-blue-500" title={`${item.linked_report_ids.length} Reports Linked`} />}
                           {item.notes && <FiFileText className="w-4 h-4 text-gray-400" title="Notes Available" />}
                         </div>
                      </div>

                      {/* Status */}
                      <div className="relative">
                        <select
                          value={item.status}
                          onChange={(e) => handleUpdateStatus(currentItemId, e.target.value)}
                          className={`w-full rounded-lg px-2 py-1 text-[10px] font-black uppercase tracking-widest border-2 transition-all appearance-none cursor-pointer ${
                            item.status === 'COMPLETED' ? 'bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-100' :
                            item.status === 'FAILED' ? 'bg-red-50 text-red-700 border-red-100 hover:bg-red-100' :
                            item.status === 'IN_PROGRESS' ? 'bg-blue-50 text-blue-700 border-blue-100 hover:bg-blue-100' :
                            'bg-gray-50 text-gray-500 border-gray-100 hover:bg-gray-100'
                          }`}
                        >
                          <option value="NOT_STARTED">Pending</option>
                          <option value="IN_PROGRESS">Testing</option>
                          <option value="COMPLETED">Passed</option>
                          <option value="FAILED">Failed</option>
                          <option value="NOT_APPLICABLE">N/A</option>
                        </select>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center justify-end gap-1 pb-1">
                        <button 
                          onClick={() => { handleOpenAssignModal(item); }} 
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all" 
                          title="Assign Researchers"
                        >
                          <FiUsers className="w-3.5 h-3.5" />
                        </button>
                        {item.status === 'FAILED' && (
                          <button 
                            onClick={(e) => { 
                              e.stopPropagation();
                              handleOpenMapReportModal(item);
                            }} 
                            className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all" 
                            title="Map Vulnerability Reports"
                          >
                            <FiLink className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button 
                          onClick={() => handleDeleteItem(currentItemId)} 
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all" 
                          title="Delete Item"
                        >
                          <FiTrash2 className="w-3.5 h-3.5" />
                        </button>
                        <button 
                          onClick={() => handleToggleExpand(currentItemId)} 
                          className={`p-1.5 rounded-lg transition-all ${isExpanded ? 'rotate-90 bg-blue-100 text-blue-700' : 'text-gray-400 hover:text-gray-900 hover:bg-gray-100'}`}
                        >
                          <FiChevronRight className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Expandable Content Layer */}
                    {isExpanded && (
                      <div className="border-t border-gray-50 bg-slate-50/50 p-6 animate-in slide-in-from-top-2 duration-200">
                        {editingItemId === currentItemId && editItemForm ? (
                          <div className="space-y-4 max-w-4xl">
                            <div className="grid grid-cols-2 gap-4">
                               <div>
                                  <label className="text-[10px] font-black text-gray-400 uppercase mb-1 block">Category</label>
                                  <input type="text" value={editItemForm.category} onChange={(e) => setEditItemForm({...editItemForm, category: e.target.value})} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-xs font-bold" />
                               </div>
                               <div>
                                  <label className="text-[10px] font-black text-gray-400 uppercase mb-1 block">Req ID</label>
                                  <input type="text" value={editItemForm.requirement_id} onChange={(e) => setEditItemForm({...editItemForm, requirement_id: e.target.value})} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-xs font-bold font-mono" />
                               </div>
                            </div>
                            <div>
                               <label className="text-[10px] font-black text-gray-400 uppercase mb-1 block">Title</label>
                               <input type="text" value={editItemForm.title} onChange={(e) => setEditItemForm({...editItemForm, title: e.target.value})} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-xs font-bold" />
                            </div>
                            <div>
                               <label className="text-[10px] font-black text-gray-400 uppercase mb-1 block">Description</label>
                               <textarea value={editItemForm.description} onChange={(e) => setEditItemForm({...editItemForm, description: e.target.value})} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-xs font-bold" rows={3} />
                            </div>
                            <div className="flex items-center gap-3 pt-2">
                               <button onClick={handleSaveEditItem} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-xs font-black shadow-lg">SAVE CHANGES</button>
                               <button onClick={handleCancelEditItem} className="bg-white text-gray-400 border border-gray-200 px-4 py-2 rounded-lg text-xs font-black">CANCEL</button>
                            </div>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div>
                              <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-3">Item Description</h4>
                              <p className="text-xs text-gray-700 leading-relaxed bg-white p-4 rounded-xl border border-gray-100 shadow-sm">{item.description || "No description provided."}</p>
                              
                              <div className="mt-6">
                                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-3">Test Objective</h4>
                                <p className="text-xs text-gray-600 italic bg-blue-50/50 p-4 rounded-xl border border-blue-100">{item.test_objective || "No objective defined."}</p>
                              </div>
                            </div>

                            <div className="space-y-6">
                              {item.expected_evidence && (
                                <div>
                                  <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2">Expected Evidence</h4>
                                  <div className="text-[11px] text-gray-600 font-medium px-1 leading-relaxed">{item.expected_evidence}</div>
                                </div>
                              )}

                              {/* Evidence & Findings (Real-time view for Admins) */}
                              <div className="rounded-2xl bg-white border border-gray-100 shadow-sm overflow-hidden">
                                 <div className="bg-gray-50 px-4 py-2 border-b border-gray-100 flex items-center justify-between">
                                    <span className="text-[10px] font-black text-gray-500 uppercase group-hover:text-blue-600 transition-colors">Researcher Findings</span>
                                    <span className={`text-[9px] font-black px-1.5 py-0.5 rounded ${item.bug_found ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                      {(item.bug_found || (item.linked_report_ids && item.linked_report_ids.length > 0)) ? 'CRITICAL BUG FOUND' : 'NO VULNERABILITIES'}
                                    </span>
                                 </div>
                                 <div className="p-4">
                                    {item.actual_evidence ? (
                                      <pre className="text-[11px] text-gray-600 bg-gray-50 p-3 rounded-lg overflow-x-auto whitespace-pre-wrap font-mono uppercase tracking-tight">
                                        {item.actual_evidence}
                                      </pre>
                                    ) : item.linked_report_ids && item.linked_report_ids.length > 0 ? (
                                      <div className="rounded-lg bg-blue-50 border border-blue-100 p-3 flex items-center gap-3">
                                        <div className="bg-blue-100 p-1.5 rounded-lg shrink-0">
                                          <FiLink className="w-3.5 h-3.5 text-blue-600" />
                                        </div>
                                        <div>
                                          <p className="text-[10px] font-black text-blue-700 uppercase tracking-widest">Evidence via Linked Reports</p>
                                          <p className="text-[11px] text-blue-600 font-medium">See reports below for technical evidence</p>
                                        </div>
                                      </div>
                                    ) : (
                                      <p className="text-xs text-gray-400 italic">No evidence uploaded yet.</p>
                                    )}

                                    {item.linked_report_ids && item.linked_report_ids.length > 0 && (
                                      <div className="mt-4 space-y-2">
                                        <h5 className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Linked Vulnerability Reports</h5>
                                        <div className="flex flex-wrap gap-2">
                                          {item.linked_report_ids.map((reportId: number) => (
                                            <button
                                              key={reportId}
                                              onClick={() => navigate(`/dashboard/reports/${reportId}`)}
                                              className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-[11px] font-bold text-blue-600 border border-blue-200 hover:border-blue-400 hover:bg-blue-50 transition-all shadow-sm active:scale-95"
                                            >
                                              <FiFileText className="w-3.5 h-3.5" />
                                              Report #{reportId}
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {item.notes && (
                                      <div className="mt-4 pt-4 border-t border-gray-100">
                                         <h5 className="text-[9px] font-black text-gray-400 uppercase mb-2">Admin/Researcher Notes</h5>
                                         <p className="text-xs text-gray-600 leading-relaxed font-medium">{item.notes}</p>
                                      </div>
                                    )}
                                 </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      {/* Assignment Modal */}
      {showAssignModal && (assigningItem || isBulkAssigning) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-gray-200 p-6">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100">
                  <FiUsers className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {isBulkAssigning ? `Bulk Assign Researchers (${selectedRunItems.size} items)` : "Assign Researchers"}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {isBulkAssigning ? "Apply selected researchers to all selected test cases." : assigningItem?.title}
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseAssignModal}
                className="rounded-lg p-2 text-gray-400 transition-all hover:bg-gray-100 hover:text-gray-600"
              >
                <FiX className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="max-h-[60vh] overflow-y-auto p-6">
              <div className="mb-4">
                <label className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <input
                    type="checkbox"
                    checked={selectedResearchers.length === 0}
                    onChange={() => setSelectedResearchers([])}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
                  />
                  <span>All Researchers (Default)</span>
                </label>
                <p className="ml-6 text-xs text-gray-500">
                  When no specific researchers are assigned, all researchers with program access can see this test case.
                </p>
              </div>

              <div className="mb-4 border-t border-gray-200 pt-4">
                <h3 className="mb-3 text-sm font-semibold text-gray-700">Or assign to specific researchers:</h3>

                {programData && programData.privateAccessUsers && programData.privateAccessUsers.length > 0 ? (
                  usersData && usersData.users && usersData.users.length > 0 ? (
                    <div className="space-y-2">
                      {usersData.users.map((user: any) => {
                        const userId = user.id;

                        return (
                          <label
                            key={userId}
                            className="flex items-center gap-3 rounded-lg border-2 border-gray-200 p-3 transition-all hover:border-blue-300 hover:bg-blue-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedResearchers.includes(userId)}
                              onChange={() => handleToggleResearcher(userId)}
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
                            />
                            <div className="flex-1">
                              <p className="text-sm font-semibold text-gray-900">
                                {user.displayName || user.username}
                              </p>
                              <p className="text-xs text-gray-600">{user.email}</p>
                            </div>
                            {selectedResearchers.includes(userId) && (
                              <FiUserCheck className="h-5 w-5 text-blue-600" />
                            )}
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="rounded-lg bg-blue-50 p-4 text-center">
                      <p className="text-sm text-blue-800">
                        Loading researchers...
                      </p>
                    </div>
                  )
                ) : (
                  <div className="rounded-lg bg-yellow-50 p-4 text-center">
                    <p className="text-sm text-yellow-800">
                      No researchers have access to this program yet. Add researchers to the program's private access list first.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 p-6">
              <button
                onClick={handleCloseAssignModal}
                className="rounded-xl border-2 border-gray-300 px-6 py-2.5 font-semibold text-gray-700 transition-all hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAssignment}
                className="rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-2.5 font-semibold text-white shadow-lg transition-all hover:from-blue-700 hover:to-blue-800 hover:shadow-xl"
              >
                Save Assignment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Slide-over Drawer for Full Master List */}
      {showMasterList && runId && (
        <div className="fixed inset-0 z-50 overflow-hidden" aria-labelledby="slide-over-title" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity" onClick={() => setShowMasterList(false)}></div>

          <div className="fixed inset-y-0 right-0 max-w-full flex">
            <div className="w-screen max-w-4xl transform transition ease-in-out duration-300 h-full flex flex-col bg-slate-50 shadow-2xl animate-in slide-in-from-right">
              {/* Drawer Header */}
              <div className="px-6 py-6 border-b border-gray-200 bg-white shadow-sm shrink-0 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-black tracking-tight text-gray-900" id="slide-over-title">Master Checklist Template</h2>
                  <p className="mt-1 flex items-center gap-2 text-sm text-gray-500 font-medium">
                    <span className="inline-block w-2 h-2 rounded-full bg-blue-500"></span>
                    {masterData?.items?.length || 0} Total Cases · {missingCount} Missing
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  {selectedMasterItems.size > 0 && (
                    <button
                      onClick={handleBulkAddFromMaster}
                      className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2 text-sm font-bold text-white shadow-md transition-all hover:bg-blue-700"
                    >
                      <FiPlus className="h-4 w-4" />
                      Add Selected ({selectedMasterItems.size})
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowMasterList(false)}
                    className="rounded-full bg-white p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all ring-1 ring-gray-200 shadow-sm"
                  >
                    <span className="sr-only">Close panel</span>
                    <FiX className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Drawer Filters/Search */}
              <div className="px-6 py-4 border-b border-gray-200 bg-white/50 backdrop-blur-sm shrink-0">
                <div className="flex flex-col md:flex-row gap-3">
                  <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                      <FiSearch className="h-4 w-4" />
                    </div>
                    <input
                      type="text"
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-xl leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all"
                      placeholder="Search test cases or categories..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <div className="relative min-w-[200px]">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                      <FiFilter className="h-4 w-4" />
                    </div>
                    <select
                      className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-xl leading-5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm appearance-none transition-all"
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                      <option value="All">All Categories</option>
                      {masterCategories.map((cat: string) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-gray-400">
                      <FiChevronDown className="h-4 w-4" />
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <button
                    onClick={handleSelectAllMaster}
                    className="text-xs font-bold text-gray-500 hover:text-blue-600 flex items-center gap-1.5 transition-colors"
                  >
                    <FiCheckSquare className="h-4 w-4" />
                    Select All Missing
                  </button>
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
                    {filteredMasterItems.length} Result{filteredMasterItems.length !== 1 && 's'}
                  </p>
                </div>
              </div>

              {/* Drawer Scrollable Content */}
              <div className="flex-1 overflow-y-auto overflow-x-hidden p-6">
                <div className="grid gap-3">
                  {filteredMasterItems.map((masterItem: any) => {
                    const inRun = isItemInRun(masterItem);
                    const itemId = masterItem.program_item_id || masterItem.item_id;
                    const isSelected = selectedMasterItems.has(itemId);

                    return (
                      <div
                        key={itemId}
                        className={`group flex items-start gap-4 rounded-2xl border p-5 transition-all ${
                          inRun 
                            ? 'bg-gray-50 border-gray-100 opacity-60' 
                            : isSelected
                              ? 'bg-blue-50 border-blue-200 shadow-sm'
                              : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-md'
                        }`}
                      >
                        {!inRun && (
                          <div 
                            className="mt-1 shrink-0 cursor-pointer text-gray-300 group-hover:text-blue-400 transition-colors"
                            onClick={() => handleToggleMasterItem(itemId)}
                          >
                            {isSelected ? (
                              <FiCheckSquare className="w-5 h-5 text-blue-600" />
                            ) : (
                              <FiSquare className="w-5 h-5" />
                            )}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[10px] font-black font-mono text-gray-400 px-1.5 py-0.5 rounded bg-gray-100 uppercase">
                              {masterItem.requirement_id || 'REQ'}
                            </span>
                            <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest bg-blue-50 px-1.5 py-0.5 rounded">
                              {masterItem.category}
                            </span>
                            {inRun && (
                              <span className="text-[9px] font-black text-emerald-600 uppercase bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100">
                                Already In Run
                              </span>
                            )}
                          </div>
                          <h4 className="text-sm font-bold text-gray-900 group-hover:text-blue-600 transition-colors">{masterItem.title}</h4>
                          <p className="mt-1 text-xs text-gray-500 line-clamp-2 leading-relaxed">{masterItem.description}</p>
                        </div>
                        {!inRun && (
                          <button
                            onClick={() => handleAddMasterItemToRun(masterItem)}
                            className="shrink-0 p-2 text-gray-300 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all"
                            title="Add to run"
                          >
                            <FiPlus className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    );
                  })}

                  {filteredMasterItems.length === 0 && (
                    <div className="py-20 text-center">
                      <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <FiSearch className="w-8 h-8 text-gray-300" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900">No matches found</h3>
                      <p className="text-gray-500 text-sm mt-1">Try adjusting your filters or search term.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Map Report Modal */}
      {showMapReportModal && mappingItem && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="w-full max-w-5xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden border border-gray-100 animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-gray-100 bg-slate-50/50 px-8 py-5 shrink-0">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-200 shrink-0">
                  <FiLink className="h-6 w-6" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-3">
                    <h2 className="text-lg font-black text-gray-900 tracking-tight uppercase">Link Program Reports</h2>
                    <span className={`text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest ${runId ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                      {runId ? "V2 Run Context" : "Program Context"}
                    </span>
                  </div>
                  <p className="mt-1 text-xs font-bold text-gray-500 truncate max-w-xl flex items-center gap-2">
                    <span className="text-blue-500">Targeting:</span> {mappingItem.title}
                  </p>
                </div>
              </div>
              <button 
                onClick={handleCloseMapReportModal}
                className="p-2.5 hover:bg-gray-200/50 rounded-full transition-all text-gray-400 hover:text-gray-600"
              >
                <FiX className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-hidden flex flex-col p-8 bg-white">
              {/* Search Bar */}
              <div className="flex items-center gap-4 mb-6 shrink-0">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                    <FiSearch className="h-4 w-4" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search vulnerabilities by ID, title, or severity..."
                    className="w-full pl-11 pr-4 py-3 text-sm font-bold bg-gray-50 border-2 border-gray-100 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all placeholder:text-gray-400"
                    value={reportSearchTerm}
                    onChange={(e) => setReportSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              {/* Table View */}
              <div className="flex-1 overflow-hidden border-2 border-gray-100 rounded-2xl bg-white shadow-inner flex flex-col">
                <div className="overflow-x-auto overflow-y-auto flex-1 custom-scrollbar">
                  <table className="w-full text-left border-collapse min-w-[800px]">
                    <thead className="sticky top-0 z-10 bg-gray-50/90 backdrop-blur-md border-b-2 border-gray-100">
                      <tr>
                        <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest w-16">Select</th>
                        <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest w-24">ID</th>
                        <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest w-32">Severity</th>
                        <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest w-40">Status</th>
                        <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest leading-tight">Report Title</th>
                        <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Global Mapping Context</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredReports.length > 0 ? (
                        filteredReports.map((report) => {
                          const isSelected = selectedReportIds.includes(report.report_id);
                          const usage = reportUsageMap.get(report.report_id);
                          
                          return (
                            <tr 
                              key={report.report_id}
                              onClick={() => handleToggleReportSelection(report.report_id)}
                              className={`group cursor-pointer transition-all hover:bg-blue-50/30 ${isSelected ? 'bg-blue-50/50' : ''}`}
                            >
                              <td className="px-6 py-4">
                                <div className={`flex h-6 w-6 items-center justify-center rounded-lg border-2 transition-all ${
                                  isSelected ? 'bg-blue-600 border-blue-600 text-white shadow-md shadow-blue-100' : 'border-gray-200 bg-white group-hover:border-blue-300'
                                }`}>
                                  {isSelected && <FiCheckSquare className="h-4 w-4" />}
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex flex-col gap-1">
                                  <span className="text-xs font-black text-gray-400 font-mono tracking-tighter">#{report.report_id}</span>
                                  {mappingItem.linked_report_ids?.includes(report.report_id) && (
                                    <span className="inline-flex items-center gap-0.5 text-[8px] font-black text-emerald-600 uppercase bg-emerald-50 px-1 py-0.25 rounded border border-emerald-100 whitespace-nowrap self-start">
                                      <FiLink className="w-2 h-2" />
                                      Linked here
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider ${
                                  report.severity_category === 'CRITICAL' ? 'bg-red-100 text-red-600 ring-1 ring-red-200' :
                                  report.severity_category === 'HIGH' ? 'bg-orange-100 text-orange-600 ring-1 ring-orange-200' :
                                  'bg-gray-100 text-gray-600 ring-1 ring-gray-200'
                                }`}>
                                  {report.severity_category || 'N/A'}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`inline-flex items-center px-2.5 py-1 rounded text-[10px] font-bold border ${
                                  report.state === 'closed' ? 'bg-gray-50 text-gray-600 border-gray-200' :
                                  report.state === 'Awaiting Fix' ? 'bg-orange-50 text-orange-700 border-orange-200' :
                                  'bg-blue-50 text-blue-700 border-blue-200'
                                }`}>
                                  {report.state || 'Triaged'}
                                  {report.retest_status && (
                                    <span className="ml-1.5 pl-1.5 border-l border-current opacity-60">
                                      {report.retest_status}
                                    </span>
                                  )}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <p className={`text-xs font-bold leading-relaxed break-words max-w-[300px] ${isSelected ? 'text-blue-900' : 'text-gray-900 group-hover:text-blue-600'}`}>
                                  {report.report_title}
                                </p>
                              </td>
                              <td className="px-6 py-4">
                                {usage && usage.length > 0 ? (
                                  <div className="space-y-2 py-1">
                                    <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest flex items-center gap-1.5">
                                      <FiLink className="w-3 h-3" />
                                      Linked in {usage.length} other checklist item{usage.length > 1 ? 's' : ''}:
                                    </p>
                                    <div className="flex flex-wrap gap-1.5">
                                      {usage.map((u, i) => (
                                        <span key={i} className="text-[10px] font-bold text-gray-600 bg-blue-50/50 px-2.5 py-1 rounded-lg break-words whitespace-normal border border-blue-100 max-w-[300px] shadow-sm">
                                          {u.title}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-[10px] font-bold text-gray-300 italic uppercase tracking-widest">No other links</span>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={6} className="py-24 text-center">
                            <div className="mx-auto w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                              <FiFileText className="h-10 w-10 text-gray-200" />
                            </div>
                            <p className="text-sm font-black text-gray-500 uppercase tracking-widest">No Matching Reports</p>
                            <p className="text-xs text-gray-400 mt-2">Adjust your filters or submit a new vulnerability report.</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between border-t border-gray-100 bg-slate-50/50 px-8 py-5 shrink-0">
              <div className="flex items-baseline gap-2">
                <span className="text-lg font-black text-blue-600">{selectedReportIds.length}</span>
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Reports Selected</span>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleCloseMapReportModal}
                  className="px-6 py-2.5 text-sm font-black text-gray-500 hover:text-gray-700 transition-all uppercase tracking-widest"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveReportMapping}
                  disabled={isUpdatingReportMapping}
                  className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-2.5 text-sm font-black text-white shadow-xl shadow-blue-200 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
                >
                  {isUpdatingReportMapping ? (
                    <div className="h-4 w-4 border-2 border-white/30 border-t-white animate-spin rounded-full" />
                  ) : (
                    <>
                      <FiSave className="w-4 h-4" />
                      Save Mapping
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel={confirmModal.confirmLabel}
        type={confirmModal.type}
      />
    </div>
  );
};

export default ProgramChecklistEditor;

